/**
 * V70 엔글 마블 V3 — SVG 아이콘 세트
 *
 * I3-α Modern Board Game 콘셉트:
 *   - 모든 아이콘 48x48 viewBox 통일
 *   - 2-tone 색상 (base + highlight) 로 깊이감
 *   - OS 이모지 플랫폼 종속성 제거
 *   - 외부 CDN 없음 (inline SVG)
 *
 * V2 유지 매핑 (eventConfig EventKind):
 *   safe[0]  = START   🏁
 *   safe[6]  = TRAP   🏝 (무인도)
 *   safe[12] = VAULT  💰 (보너스 금고)
 *   safe[18] = SHOP   🏪 (아이템 상점)
 *   E1  확정 vs 랜덤   🎯
 *   E2  가위바위보     ✊
 *   E3  보물상자       🎁
 *   E4  동전           🪙
 *   E5  폭탄           💣
 *   E6  카드           🃏
 *   E7  홀짝           🔢
 *   E8  방패           🛡
 *   E9  워프           🌀
 *   E10 잭팟           💎
 */
import React from 'react';
import type { EventKind } from '../config/eventConfig';
// COLORS 토큰은 별도 파일에서 참조하지 않음 (이번 아이콘 세트는 자체 색상 포함)

export interface MarbleIconProps {
  size?: number;
  variant?: 'solid' | 'outline';
  className?: string;
}

type IconFn = React.FC<MarbleIconProps>;

// ─────────── Primitives ───────────

function Svg({ size = 32, children, className }: { size?: number; children: React.ReactNode; className?: string }) {
  return (
    <svg viewBox="0 0 48 48" width={size} height={size} className={className} aria-hidden="true" style={{ display: 'block' }}>
      {children}
    </svg>
  );
}

// ─────────── Special corners ───────────

export const IconStart: IconFn = ({ size = 32 }) => (
  <Svg size={size}>
    {/* 깃대 + 깃발 */}
    <rect x="12" y="8" width="3" height="32" fill="#065f46" rx="1" />
    <path d="M15 10 Q25 7 35 12 L35 22 Q25 19 15 22 Z" fill="#dc2626" stroke="#991b1b" strokeWidth="1" />
    {/* 체크무늬 */}
    <rect x="18" y="12" width="4" height="4" fill="#fff" />
    <rect x="26" y="12" width="4" height="4" fill="#fff" />
    <rect x="22" y="16" width="4" height="4" fill="#fff" />
    <rect x="30" y="16" width="4" height="4" fill="#fff" />
    {/* 바닥 */}
    <ellipse cx="13" cy="41" rx="4" ry="1.5" fill="#065f46" opacity="0.3" />
  </Svg>
);

export const IconTrap: IconFn = ({ size = 32 }) => (
  <Svg size={size}>
    {/* 섬 바닥 */}
    <ellipse cx="24" cy="38" rx="18" ry="4" fill="#78350f" />
    <ellipse cx="24" cy="36" rx="16" ry="3.5" fill="#f59e0b" />
    {/* 야자수 줄기 */}
    <path d="M22 36 Q22 24 24 14" stroke="#78350f" strokeWidth="2.5" fill="none" strokeLinecap="round" />
    {/* 야자수 잎 */}
    <path d="M24 14 Q18 11 14 14 Q18 16 24 14" fill="#15803d" stroke="#166534" strokeWidth="0.5" />
    <path d="M24 14 Q30 11 34 14 Q30 16 24 14" fill="#15803d" stroke="#166534" strokeWidth="0.5" />
    <path d="M24 14 Q22 7 18 7 Q22 11 24 14" fill="#15803d" stroke="#166534" strokeWidth="0.5" />
    <path d="M24 14 Q26 7 30 7 Q26 11 24 14" fill="#15803d" stroke="#166534" strokeWidth="0.5" />
    {/* 코코넛 */}
    <circle cx="23" cy="15" r="1.5" fill="#78350f" />
    <circle cx="26" cy="15" r="1.5" fill="#78350f" />
    {/* 파도 */}
    <path d="M4 42 Q10 40 16 42 T28 42 T40 42 T44 42" stroke="#0ea5e9" strokeWidth="1" fill="none" opacity="0.6" />
  </Svg>
);

export const IconVault: IconFn = ({ size = 32 }) => (
  <Svg size={size}>
    {/* 금고 본체 */}
    <rect x="8" y="10" width="32" height="30" fill="#78350f" rx="2" />
    <rect x="10" y="12" width="28" height="26" fill="#92400e" rx="1" />
    {/* 문 */}
    <rect x="12" y="14" width="24" height="22" fill="#b45309" rx="1" />
    {/* 다이얼 */}
    <circle cx="24" cy="24" r="7" fill="#78350f" stroke="#fbbf24" strokeWidth="1.5" />
    <circle cx="24" cy="24" r="2" fill="#fbbf24" />
    {/* 다이얼 눈금 */}
    <circle cx="24" cy="18" r="0.8" fill="#fbbf24" />
    <circle cx="30" cy="24" r="0.8" fill="#fbbf24" />
    <circle cx="24" cy="30" r="0.8" fill="#fbbf24" />
    <circle cx="18" cy="24" r="0.8" fill="#fbbf24" />
    {/* 손잡이 */}
    <line x1="24" y1="24" x2="28" y2="20" stroke="#fbbf24" strokeWidth="1.5" strokeLinecap="round" />
    {/* 다리 */}
    <rect x="10" y="40" width="4" height="4" fill="#78350f" />
    <rect x="34" y="40" width="4" height="4" fill="#78350f" />
  </Svg>
);

export const IconShop: IconFn = ({ size = 32 }) => (
  <Svg size={size}>
    {/* 지붕 */}
    <path d="M4 20 L24 8 L44 20 Z" fill="#8b5cf6" stroke="#6d28d9" strokeWidth="1" />
    <path d="M8 20 L24 10 L40 20 Z" fill="#a78bfa" />
    {/* 벽 */}
    <rect x="8" y="20" width="32" height="22" fill="#f3e8ff" />
    {/* 창문 */}
    <rect x="11" y="24" width="10" height="8" fill="#c4b5fd" stroke="#8b5cf6" strokeWidth="0.5" />
    <rect x="27" y="24" width="10" height="8" fill="#c4b5fd" stroke="#8b5cf6" strokeWidth="0.5" />
    {/* 문 */}
    <rect x="20" y="32" width="8" height="10" fill="#6b21a8" />
    <circle cx="26" cy="37" r="0.6" fill="#fbbf24" />
    {/* 간판 */}
    <rect x="16" y="18" width="16" height="4" fill="#fbbf24" stroke="#d97706" strokeWidth="0.5" rx="0.5" />
    <text x="24" y="21.5" fontSize="3" textAnchor="middle" fill="#78350f" fontWeight="bold">SHOP</text>
  </Svg>
);

// ─────────── E1 ~ E10 ───────────

export const IconE1: IconFn = ({ size = 32 }) => (
  <Svg size={size}>
    {/* 두 개의 문 */}
    <rect x="7" y="10" width="14" height="30" fill="#065f46" stroke="#064e3b" strokeWidth="1" rx="1" />
    <rect x="27" y="10" width="14" height="30" fill="#5b21b6" stroke="#4c1d95" strokeWidth="1" rx="1" />
    {/* 손잡이 */}
    <circle cx="18" cy="25" r="1.5" fill="#fbbf24" />
    <circle cx="30" cy="25" r="1.5" fill="#fbbf24" />
    {/* 라벨 */}
    <text x="14" y="30" fontSize="6" fill="#86efac" fontWeight="bold" textAnchor="middle">A</text>
    <text x="34" y="30" fontSize="6" fill="#c4b5fd" fontWeight="bold" textAnchor="middle">B</text>
  </Svg>
);

// V70 2026-10-20 (긴급 재수정) — 가위바위보 아이콘.
//   사용자 지시: OS 이모지 ✌️ ✊ ✋ 세 개를 한 줄에 균형 배치. SVG path 직접 제작/변형 금지.
//   원형 배경 제거 — 보드 칸 자체 배경/레이아웃 유지, 손 그림 뒤 별도 배경 X.
//   48x48 viewBox 유지 — 세 이모지를 가로 균등 분할 (x=10, 24, 38) + 중앙 수직 정렬.
//   폰트 패밀리: OS native color emoji 폰트 체인 (Apple Color Emoji / Segoe UI Emoji / Noto Color Emoji).
export const IconE2: IconFn = ({ size = 32 }) => (
  <Svg size={size}>
    <text
      x="10"
      y="30"
      fontSize="14"
      textAnchor="middle"
      fontFamily='"Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif'
    >
      {'✌️'}
    </text>
    <text
      x="24"
      y="30"
      fontSize="14"
      textAnchor="middle"
      fontFamily='"Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif'
    >
      {'✊'}
    </text>
    <text
      x="38"
      y="30"
      fontSize="14"
      textAnchor="middle"
      fontFamily='"Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif'
    >
      {'✋'}
    </text>
  </Svg>
);

export const IconE3: IconFn = ({ size = 32 }) => (
  <Svg size={size}>
    {/* 보물상자 */}
    <rect x="8" y="20" width="32" height="20" fill="#78350f" stroke="#451a03" strokeWidth="1" rx="2" />
    <rect x="8" y="18" width="32" height="8" fill="#92400e" stroke="#451a03" strokeWidth="1" rx="2" />
    {/* 금속 밴드 */}
    <rect x="8" y="24" width="32" height="2" fill="#fbbf24" />
    <rect x="22" y="18" width="4" height="22" fill="#fbbf24" />
    {/* 자물쇠 */}
    <rect x="21" y="26" width="6" height="5" fill="#eab308" stroke="#78350f" strokeWidth="0.5" rx="1" />
    <circle cx="24" cy="28.5" r="0.8" fill="#78350f" />
    {/* 반짝임 */}
    <circle cx="12" cy="22" r="0.8" fill="#fde68a" />
    <circle cx="36" cy="36" r="0.8" fill="#fde68a" />
  </Svg>
);

export const IconE4: IconFn = ({ size = 32 }) => (
  <Svg size={size}>
    {/* 동전 */}
    <ellipse cx="24" cy="24" rx="14" ry="14" fill="#fbbf24" stroke="#b45309" strokeWidth="1.5" />
    <ellipse cx="24" cy="24" rx="11" ry="11" fill="#f59e0b" />
    {/* 1원 심볼 */}
    <text x="24" y="30" fontSize="18" fill="#78350f" fontWeight="bold" textAnchor="middle" fontFamily="serif">¢</text>
    {/* 반짝임 */}
    <ellipse cx="18" cy="18" rx="2" ry="4" fill="#fef3c7" opacity="0.5" transform="rotate(-30 18 18)" />
  </Svg>
);

export const IconE5: IconFn = ({ size = 32 }) => (
  <Svg size={size}>
    {/* 폭탄 */}
    <circle cx="22" cy="28" r="12" fill="#1f2937" stroke="#111827" strokeWidth="1" />
    <ellipse cx="18" cy="24" rx="3" ry="4" fill="#4b5563" opacity="0.6" />
    {/* 심지 */}
    <path d="M27 18 Q30 14 33 12" stroke="#78350f" strokeWidth="1.5" fill="none" strokeLinecap="round" />
    {/* 불꽃 */}
    <path d="M33 12 L35 9 L33 11 L34 7 L32 10 L30 8 L32 11 Z" fill="#fbbf24" stroke="#dc2626" strokeWidth="0.5" />
    <circle cx="33" cy="11" r="1.5" fill="#fef3c7" opacity="0.8" />
  </Svg>
);

export const IconE6: IconFn = ({ size = 32 }) => (
  <Svg size={size}>
    {/* 카드 뒤 */}
    <rect x="8" y="10" width="14" height="22" fill="#4f46e5" stroke="#312e81" strokeWidth="1" rx="2" transform="rotate(-10 15 21)" />
    <rect x="18" y="12" width="14" height="22" fill="#7c3aed" stroke="#5b21b6" strokeWidth="1" rx="2" />
    <rect x="28" y="14" width="14" height="22" fill="#c026d3" stroke="#86198f" strokeWidth="1" rx="2" transform="rotate(10 35 25)" />
    {/* 뒷면 패턴 */}
    <path d="M25 15 L25 31 M20 23 L30 23" stroke="#c4b5fd" strokeWidth="1" opacity="0.5" />
    <circle cx="25" cy="23" r="2" fill="none" stroke="#c4b5fd" strokeWidth="1" opacity="0.5" />
  </Svg>
);

export const IconE7: IconFn = ({ size = 32 }) => (
  <Svg size={size}>
    {/* 홀/짝 공 */}
    <circle cx="16" cy="24" r="8" fill="#dc2626" stroke="#7f1d1d" strokeWidth="1" />
    <text x="16" y="28" fontSize="10" fill="#fef2f2" fontWeight="bold" textAnchor="middle">홀</text>
    <circle cx="32" cy="24" r="8" fill="#0369a1" stroke="#082f49" strokeWidth="1" />
    <text x="32" y="28" fontSize="10" fill="#f0f9ff" fontWeight="bold" textAnchor="middle">짝</text>
    {/* 반짝임 */}
    <ellipse cx="13" cy="20" rx="1.5" ry="2.5" fill="#fef2f2" opacity="0.4" />
    <ellipse cx="29" cy="20" rx="1.5" ry="2.5" fill="#f0f9ff" opacity="0.4" />
  </Svg>
);

export const IconE8: IconFn = ({ size = 32 }) => (
  <Svg size={size}>
    {/* 방패 */}
    <path d="M24 6 L10 12 L10 24 Q10 36 24 42 Q38 36 38 24 L38 12 Z" fill="#4f46e5" stroke="#312e81" strokeWidth="1.5" />
    <path d="M24 10 L14 15 L14 24 Q14 32 24 38 Q34 32 34 24 L34 15 Z" fill="#6366f1" />
    {/* 중앙 엠블럼 */}
    <path d="M24 16 L21 22 L18 20 L21 24 L18 28 L24 26 L30 28 L27 24 L30 20 L27 22 Z" fill="#fbbf24" stroke="#92400e" strokeWidth="0.5" />
  </Svg>
);

export const IconE9: IconFn = ({ size = 32 }) => (
  <Svg size={size}>
    {/* 포털 */}
    <defs>
      <radialGradient id="warp-g" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#f0abfc" />
        <stop offset="50%" stopColor="#c026d3" />
        <stop offset="100%" stopColor="#581c87" />
      </radialGradient>
    </defs>
    <ellipse cx="24" cy="24" rx="18" ry="12" fill="url(#warp-g)" stroke="#581c87" strokeWidth="1.5" />
    <ellipse cx="24" cy="24" rx="14" ry="9" fill="none" stroke="#f0abfc" strokeWidth="1" opacity="0.6" />
    <ellipse cx="24" cy="24" rx="9" ry="6" fill="none" stroke="#fef2f2" strokeWidth="1" opacity="0.5" />
    <ellipse cx="24" cy="24" rx="5" ry="3" fill="#000000" opacity="0.5" />
    {/* 별 */}
    <circle cx="18" cy="20" r="0.8" fill="#fef2f2" />
    <circle cx="30" cy="26" r="0.8" fill="#fef2f2" />
    <circle cx="26" cy="18" r="0.6" fill="#fef2f2" />
  </Svg>
);

export const IconE10: IconFn = ({ size = 32 }) => (
  <Svg size={size}>
    {/* 다이아몬드 */}
    <path d="M24 8 L36 20 L24 42 L12 20 Z" fill="#22d3ee" stroke="#0e7490" strokeWidth="1.5" />
    <path d="M24 8 L12 20 L36 20 Z" fill="#67e8f9" />
    <path d="M18 20 L24 8 L30 20 L24 32 Z" fill="#06b6d4" opacity="0.5" />
    {/* 하이라이트 */}
    <path d="M18 20 L22 15" stroke="#fff" strokeWidth="1" opacity="0.8" />
    <path d="M30 20 L26 15" stroke="#fff" strokeWidth="1" opacity="0.5" />
    {/* 반짝임 */}
    <circle cx="36" cy="14" r="1" fill="#fbbf24" />
    <circle cx="40" cy="18" r="0.6" fill="#fef3c7" />
    <circle cx="10" cy="30" r="0.8" fill="#fbbf24" />
  </Svg>
);

// ─────────── 매퍼 ───────────

export function CellIcon({ kind, index, size = 32 }: { kind: EventKind; index: number; size?: number }) {
  if (kind === 'safe') {
    if (index === 0) return <IconStart size={size} />;
    if (index === 6) return <IconTrap size={size} />;
    if (index === 12) return <IconVault size={size} />;
    if (index === 18) return <IconShop size={size} />;
  }
  switch (kind) {
    case 'E1': return <IconE1 size={size} />;
    case 'E2': return <IconE2 size={size} />;
    case 'E3': return <IconE3 size={size} />;
    case 'E4': return <IconE4 size={size} />;
    case 'E5': return <IconE5 size={size} />;
    case 'E6': return <IconE6 size={size} />;
    case 'E7': return <IconE7 size={size} />;
    case 'E8': return <IconE8 size={size} />;
    case 'E9': return <IconE9 size={size} />;
    case 'E10': return <IconE10 size={size} />;
    case 'vault': return <IconVault size={size} />;
    case 'shop': return <IconShop size={size} />;
    default: return null;
  }
}
